import { GuildApplicationEntity } from '../../types/guilds/guild-application-entity';
import { GuildInviteEntity } from '../../types/guilds/guild-invite-entity';
import { GuildLeaderboardEntity } from '../../types/guilds/guild-leaderboard-entity';
import { GuildMemberEntity } from '../../types/guilds/guild-member-entity';
import { SoloLeaderboardEntity } from '../../types/guilds/solo-leaderboard-entity';
import { PaginationParams, PaginationResponse } from '../../types/pagination';
import { api } from '../api';
import {
  GetGuildApplicationsAdditionalParams,
  GetGuildLeaderboardAdditionalParams,
  GetSoloLeaderboardAdditionalParams,
  GuildProfile,
  GuildSocialProfile,
  IncomingRequestsResponse,
  InviteUserToGuildProps,
  JoinToGuildProps,
  UpdateGuildMemberFeeParams,
} from './types';

export const GuildApi = {
  leave: async () => {
    const { data } = await api.post('/guild/leave');

    return data;
  },
  invitePlayerToGuild: async (params: InviteUserToGuildProps) => {
    const { data } = await api.post('/guild/player/invite', params);

    return data;
  },
  joinToGuild: async ({ guildId, gpTax }: JoinToGuildProps) => {
    const { data } = await api.post(`/guild/${guildId}/apply`, { gpTax });

    return data;
  },

  guildManagement: async () => {
    const { data } = await api.get('/guild/management');

    return data;
  },

  guildArchive: async () => {
    const { data } = await api.post('/guild/archive');

    return data;
  },

  guildOverview: async () => {
    const { data } = await api.get('/guild/overview');

    return data;
  },

  inviteUserToGuild: async (params: InviteUserToGuildProps) => {
    await api.post('/guild/player/invite', params);

    return { username: params.inviteeName };
  },

  prizePool: async () => {
    const { data } = await api.get('/guild/pool/power');

    return data;
  },

  guildOffersProposals: async () => {
    const { data } = await api.get('/guild/offers/proposals');

    return data;
  },

  getGuildLeaderboard: async ({
    isOnlyAvailableGuild,
    ...params
  }: PaginationParams & GetGuildLeaderboardAdditionalParams): Promise<PaginationResponse<GuildLeaderboardEntity>> => {
    const url = isOnlyAvailableGuild === 'true'
      ? '/guild/leaderboard/free'
      : '/guild/leaderboard';

    const { data } = await api.get<PaginationResponse<GuildLeaderboardEntity>>(
      url,
      { params },
    );

    return data;
  },

  getSoloLeaderboard: async ({
    hideFounders,
    availablePlayers,
    ...params
  }: PaginationParams & GetSoloLeaderboardAdditionalParams): Promise<PaginationResponse<SoloLeaderboardEntity>> => {
    let url = '/solo/leaderboard';

    if (hideFounders === 'true') {
      url = '/solo/leaderboard/non-founders';
    }

    if (availablePlayers === 'true') {
      url = '/solo/leaderboard/free';
    }

    const { data } = await api.get<PaginationResponse<SoloLeaderboardEntity>>(
      url,
      { params },
    );

    return data;
  },

  getGuildInvites: async (params: PaginationParams): Promise<PaginationResponse<GuildInviteEntity>> => {
    const { data } = await api.get<PaginationResponse<GuildInviteEntity>>('/guild/invites', { params });

    return data;
  },

  acceptGuildInvite: async (inviteId: string) => {
    const { data } = await api.post(`/guild/invite/${inviteId}/accept`);

    return data;
  },

  declineGuildInvite: async (inviteId: string) => {
    const { data } = await api.post(`/guild/invite/${inviteId}/decline`);

    return data;
  },

  getGuildApplications: async ({
    search,
    ...params
  }: PaginationParams & GetGuildApplicationsAdditionalParams): Promise<PaginationResponse<GuildApplicationEntity>> => {
    const { data } = await api.get<PaginationResponse<GuildApplicationEntity>>(
      '/guild/applications',
      {
        params: {
          ...params,
          search,
          sort_by: params.sort_by,
          sort_direction: params.sort_direction,
        },
      },
    );

    return data;
  },

  acceptGuildApplication: async (applicationId: string) => {
    const { data } = await api.post(`/guild/application/${applicationId}/accept`);

    return data;
  },

  declineGuildApplication: async (applicationId: string) => {
    const { data } = await api.post(`/guild/application/${applicationId}/decline`);

    return data;
  },

  getGuildMembers: async (): Promise<GuildMemberEntity[]> => {
    const { data } = await api.get<GuildMemberEntity[]>('/guild/members');

    return data;
  },

  debugGuild: async (pixionId: string) => {
    const { data } = await api.get(`/debug/guild/${pixionId}/new`);

    return data;
  },

  kickUserFromGuild: async (playerId: string) => {
    const { data } = await api.post(`/guild/player/${playerId}/kick`);

    return data;
  },

  getIncomingInviteRequests: async (): Promise<IncomingRequestsResponse> => {
    const { data } = await api.get<IncomingRequestsResponse>('/guild/invites/incoming');

    return data;
  },

  getIncomingApplicationRequests: async (): Promise<IncomingRequestsResponse> => {
    const { data } = await api.get<IncomingRequestsResponse>('/guild/applications/incoming');

    return data;
  },

  getSentGuildInvites: async ({
    search,
    ...params
  }: PaginationParams & GetGuildApplicationsAdditionalParams): Promise<PaginationResponse<GuildApplicationEntity>> => {
    const { data } = await api.get<PaginationResponse<GuildApplicationEntity>>(
      '/guild/invites/sent',
      {
        params: {
          ...params,
          search,
          sort_by: params.sort_by,
          sort_direction: params.sort_direction,
        },
      },
    );

    return data;
  },

  setGuildProfile: async (params: GuildProfile) => {
    const requestParams: [string, GuildProfile | GuildSocialProfile] = params.name
      ? ['/guild/name', params]
      : ['/guild/profile', params.profile];
    const { data } = await api.post(...requestParams);

    return data;
  },

  getGuildProfile: async (guildId: string) => {
    const { data } = await api.get(`/guild/${guildId}/profile`);

    return data;
  },

  updateGuildMemberFee: async (params: UpdateGuildMemberFeeParams) => {
    const { data } = await api.post('/guild/member/fee/propose', params);

    return data;
  },
};
